/**
 * OutputSection — the card around a read-only generated value (setup SQL, public
 * key, fingerprint). Mirrors FormSection's look but carries no form state: the
 * Copy/Download actions sit in the header row (right of the label), and `children`
 * is the rendered code, shown in a scrollable box.
 */
import * as React from 'react';

import { Label } from '@/_shared/components/ui/label';
import { cn } from '@/_shared/lib/utils';

export interface OutputSectionProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  /** Header actions (Copy/Download), rendered right of the label. */
  action?: React.ReactNode;
  /** The rendered code — a `<pre>`, `<code>`, or syntax-highlighted block. */
  children: React.ReactNode;
  className?: string;
  /** Render the content as an error message: red text, no code-box chrome. */
  error?: boolean;
}

export function OutputSection({
  label,
  description,
  action,
  children,
  className,
  error = false,
}: OutputSectionProps): React.JSX.Element {
  return (
    <div className={cn('grid gap-2 rounded-xl border border-grey-50 bg-grey-25 p-4', className)}>
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="grid gap-1">
          <Label>{label}</Label>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {action}
      </div>
      <div
        className={cn(
          '-mx-2 -mb-2 max-h-80 overflow-auto rounded-md border px-3 py-2',
          error ? 'border-danger-border bg-danger-bg text-xs text-destructive' : 'border-grey-75 bg-grey-0',
        )}
      >
        {children}
      </div>
    </div>
  );
}
